import { Exclude, Transform, Type } from 'class-transformer';
import { Column, Index } from 'typeorm';

import { ApiProperty } from '../../codegen';
import { LangDto, UTMDto } from '../../dtos';
import { ELeague } from '../../enums';
import {
  IsDate,
  IsDateString,
  IsEmail,
  CustomValidateName,
  IsOptional,
  IsString,
  IsBoolean,
  IsObject,
  ValidateNested,
  IsNumber,
  IsArray,
  Min,
  Max,
} from '../../validators';
import { OauthDto } from '../onboard/oauth.dtos';
import { TAchievementId } from '../achievements/achievements.registry';

import { BasicEntity } from './basic.entity';

export class FlagsDto {
  @ApiProperty()
  @IsOptional()
  @IsBoolean()
  firstDepositBonusProcessed?: boolean;
}

export class MqlQuizDto {
  @ApiProperty()
  @IsOptional()
  @IsBoolean()
  requiresDateOfBirth?: boolean;
}

// Game stats for matchmaking
export class GameStatsDto {
  @IsOptional()
  @IsNumber()
  gamesPlayed?: number;

  @IsOptional()
  @IsNumber()
  wins?: number;

  @IsOptional()
  @IsNumber()
  losses?: number;

  @IsOptional()
  @IsNumber()
  winRate?: number;

  // Rating per game: Record<gameId, rating>
  @IsOptional()
  @IsObject()
  ratingsPerGame?: Record<string, number>;

  // Safe leagues: minimum league reached per game
  @IsOptional()
  @IsObject()
  minLeaguePerGame?: Record<string, ELeague>;

  // Games played count per game
  @IsOptional()
  @IsObject()
  gamesPerGame?: Record<number, number>;

  // Last played time per game (ISO string)
  @IsOptional()
  @IsObject()
  lastPlayedAt?: Record<number, string>;

  // Current server-side bot difficulty per game (0..1)
  @IsOptional()
  @IsObject()
  botDifficulty01PerGame?: Record<string, number>;

  @IsOptional()
  @IsNumber()
  peakRating?: number;

  @IsOptional()
  @IsNumber()
  avgScore?: number;

  @IsOptional()
  @IsNumber()
  bestScore?: number;
}

// Match preferences
export class MatchPreferencesDto {
  @IsOptional()
  @IsNumber()
  preferredGameMode?: number;

  @IsOptional()
  @IsString()
  preferredRegion?: string;

  @IsOptional()
  @IsNumber()
  maxWaitTime?: number;

  @IsOptional()
  @IsNumber()
  skillTolerance?: number;

  @IsOptional()
  @IsBoolean()
  allowCrossRegion?: boolean;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  excludedRegions?: string[];
}

// User achievements: Record<achievementId, earnedAt>
export type TAchievementsDto = Record<TAchievementId, Date>;

export class BasicUserSettingsDto {
  @IsOptional()
  @IsBoolean()
  mfaEnabled?: boolean;

  @IsOptional()
  @IsString()
  mfaMethod?: string;

  @IsOptional()
  @IsString()
  encryptedTotpSecret?: string;
}

export class BasicUser extends BasicEntity {
  @ApiProperty()
  @IsEmail()
  @Column({ type: 'varchar', nullable: true, unique: false })
  @Index({ unique: true, where: `"deletedAt" is NULL AND email IS NOT NULL` })
  @Transform(({ value }) => String(value).toLowerCase())
  email: string;

  @ApiProperty()
  @IsDate()
  @IsOptional()
  @Column({ type: 'timestamptz', precision: 3, nullable: true })
  registeredAt?: Date;

  @ApiProperty()
  @IsOptional()
  @IsDateString()
  @Transform(({ value }) => {
    if (value == null) return null;
    if (typeof value === 'string') return value.slice(0, 10);
    if (value instanceof Date) return value.toISOString().slice(0, 10);

    return value;
  })
  @Column({ type: 'date', nullable: true })
  dateOfBirth?: string | null;

  @ApiProperty()
  @IsOptional()
  @IsString()
  @CustomValidateName()
  @Index({ unique: true, where: `"deletedAt" is NULL AND "name" <> ''` })
  @Column({ type: 'varchar', nullable: false, default: '' })
  name: string;

  @ApiProperty()
  @IsOptional()
  @IsString()
  @Index({
    unique: true,
    where: '"deletedAt" is NULL AND "tempId" IS NOT NULL',
  })
  @Column({ type: 'uuid', nullable: true, default: null })
  tempId: string | null;

  @ApiProperty()
  @Type(() => OauthDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  oauth: OauthDto;

  @IsOptional()
  @IsBoolean()
  @Column({ type: 'boolean', nullable: false, default: false })
  isShadowed: boolean; // for inviting users who are not yet in the system

  @IsBoolean()
  @Column({ type: 'boolean', nullable: false, default: false })
  isTestUser: boolean;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', nullable: false, default: '' })
  password?: string;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', nullable: false, default: '' })
  passwordPrevious?: string;

  @Exclude()
  @Column({ type: 'int', nullable: false, default: 0 })
  passwordAttempts: number;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'timestamptz', nullable: true })
  blockedUntil: Date | null;

  @Exclude()
  @IsOptional()
  @IsString()
  @Index({
    unique: true,
    where: '"deletedAt" is NULL AND "linkRestore" IS NOT NULL',
  })
  @Column({ type: 'varchar', nullable: true })
  linkRestore?: string | null;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'timestamptz', nullable: true })
  linkRestoreResendAfter?: Date | null;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', nullable: false, default: '' })
  linkActivation?: string;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'timestamptz', nullable: true })
  linkActivationResendAfter?: Date | null;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'timestamptz', nullable: true })
  linkActivationLiveUntil?: Date | null;

  @Exclude()
  @Column({ type: 'int', nullable: false, default: 0 })
  linkActivationAttempts: number;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'timestamptz', nullable: true })
  blockLinkActivationUntil?: Date;

  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', nullable: false, default: '' })
  avatar?: string;

  @IsOptional()
  @IsBoolean()
  @Column({ type: 'boolean', nullable: false, default: true })
  isActive?: boolean;

  @IsObject()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  utm: UTMDto;

  @IsObject()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  lang: LangDto;

  @Type(() => BasicUserSettingsDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  settings: BasicUserSettingsDto;

  @Exclude()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', nullable: true })
  withdrawalKycVerificationId?: string | null;

  @ApiProperty()
  @IsOptional()
  @Type(() => MqlQuizDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  regData: MqlQuizDto;

  @ApiProperty()
  @IsOptional()
  @Type(() => FlagsDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  flags: FlagsDto;

  // Game stats
  @ApiProperty()
  @IsOptional()
  @Type(() => GameStatsDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  gameStats: GameStatsDto;

  // User achievements (separate from gameStats)
  @ApiProperty()
  @IsObject()
  @IsOptional()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  achievements: Record<TAchievementId, Date>;

  // Matchmaking preferences
  @ApiProperty()
  @IsOptional()
  @Type(() => MatchPreferencesDto)
  @ValidateNested()
  @Column({ type: 'jsonb', nullable: false, default: {} })
  matchPreferences: MatchPreferencesDto;

  // Current matchmaking status
  @IsOptional()
  @IsBoolean()
  @Column({ type: 'boolean', nullable: false, default: false })
  isInMatchmaking: boolean;

  // Match search start time
  @IsOptional()
  @IsDate()
  @Column({ type: 'timestamptz', nullable: true })
  matchmakingStartedAt?: Date;

  // Current game mode for search
  @IsOptional()
  @IsString()
  @Column({ type: 'uuid', nullable: true })
  currentGameMode?: string;

  /**
   * When set (0..1), user only matches bots at this engine difficulty; null = normal matchmaking.
   */
  @ApiProperty()
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  @Column({
    name: 'promo_difficulty',
    type: 'double precision',
    nullable: true,
  })
  promoDifficulty?: number | null;
}
